import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { AsLabel, CaptionLG, CaptionMD, ParagraphSM, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { AngelDialog } from '@/molecules/AngelDialog'
import { useToast } from '@/molecules/Toast'
import { ShareMomentSocials } from '@/organisms/ShareMoment/ShareMomentDialog/ShareMomentSocials'
import { createMomentShareDeepLink } from '@/services/DeepLinkService'
import { MomentUserStory } from '@/services/MomentsService/types'
import { Moment } from '@/types/codegen-federation'
import { PlayableStory } from '@/utils/StoryUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface ShareMomentDialogProps {
  isOpen: boolean
  onClose: () => void
  moment: Moment
  userId?: string
  story?: PlayableStory | MomentUserStory
}

export const ShareMomentDialog: React.FC<ShareMomentDialogProps> = ({ isOpen, onClose, moment, story, userId }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')
  const [shareUrl, setShareUrl] = useState<string | null>()
  const toast = useToast()

  useEffect(() => {
    if (moment) {
      createMomentShareDeepLink(
        {
          contentName: moment.title as string,
          guid: moment.episode?.guid as string,
          focusedOwnerId: userId,
          momentId: moment.id as string,
          path: story
            ? `${paths.share.story}/${story.id}`
            : userId
            ? `${paths.share.user}/${userId}/moment/${moment.id}`
            : `${paths.share.moment}/${moment.id}`,
          projectSlug: moment.episode?.projectSlug as string,
          timeSec: moment.startTime as number,
        },
        { channel: 'angel-web', feature: 'moment-share', campaign: 'digital-collectibles' },
      ).then((result) => {
        setShareUrl(result)
      })
    }
  }, [moment, story, userId])

  const linkCopied = t('linkCopied', 'Link copied.')

  const trackShareClick = (network: string) => {
    track('Moment Share Clicked', {
      type: network,
      momentId: moment?.id,
      momentTitle: moment?.title,
      shareUserId: userId,
      projectSlug: moment?.episode?.projectSlug,
      episodeGuid: moment?.episode?.guid,
    })
  }

  return (
    <AngelDialog
      bodyClassName={classNames(
        'bg-core-gray-900 border border-core-gray-800 shadow-modal',
        'md:w-[500px] md:!relative',
        '!fixed !bottom-0 left-0 right-0 !max-w-full',
      )}
      title={t('share', 'Share')}
      open={isOpen}
      onClose={onClose}
    >
      <CaptionMD color="core-gray-400">
        <Translate i18nKey="sceneNameLabel" t={t}>
          Scene name:
        </Translate>
      </CaptionMD>
      <TitleSM weight="semibold" color="white" className="mb-6">
        {moment?.title}
      </TitleSM>

      <ShareMomentSocials url={shareUrl} trackShareClick={trackShareClick} />

      <CaptionMD color="core-gray-400" className="mb-2">
        <Translate t={t} i18nKey="copyUrlLabel">
          Copy URL:
        </Translate>
      </CaptionMD>
      <div className="flex w-full gap-3">
        <div className="shrink-1 grow-1 overflow-hidden rounded-lg border border-core-gray-700 bg-core-gray-950 p-3">
          <ParagraphSM color="core-gray-500" className="line-clamp-1 overflow-hidden text-ellipsis">
            {shareUrl}
          </ParagraphSM>
        </div>
        <div
          className="ml-auto cursor-pointer rounded-lg bg-core-oxide-bright px-6 py-3.5 hover:bg-core-oxide-bright/80 active:bg-core-oxide-bright/75"
          onClick={() => {
            trackShareClick('Copy to Clipboard')
            navigator.clipboard.writeText(shareUrl as string)
            toast?.showToast(linkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
          }}
        >
          <CaptionLG as={AsLabel} weight="bold" color="core-gray-950" className="cursor-pointer whitespace-nowrap">
            <Translate i18nKey="copyUrl" t={t}>
              Copy URL
            </Translate>
          </CaptionLG>
        </div>
      </div>
    </AngelDialog>
  )
}
